import React from "react";
import { Bell, AlertCircle, CheckCircle2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";

export default function NotificationsBell() {
  const { can } = useAuth();
  const [data, setData] = React.useState({ count: 0, critical_count: 0, items: [] });
  const [loading, setLoading] = React.useState(false);
  const detailsRef = React.useRef(null);
  const location = useLocation();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/dashboard/notifications");
      setData(response.data || { count: 0, critical_count: 0, items: [] });
    } catch (error) {
      // Notification failure must never block the main workflow.
      console.error("[notifications] load failed:", error?.response?.status || error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const timer = window.setInterval(load, 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [load]);

  React.useEffect(() => {
    if (detailsRef.current) detailsRef.current.open = false;
    load();
  }, [location.pathname, load]);

  const count = Number(data.count || 0);
  const critical = Number(data.critical_count || 0);

  return (
    <details ref={detailsRef} className="relative" data-testid="notifications-bell">
      <summary
        className="relative grid size-10 cursor-pointer list-none place-items-center rounded-xl border border-border/70 bg-card/60 text-muted-foreground shadow-[0_1px_2px_hsl(var(--foreground)/0.03)] transition hover:border-primary/25 hover:bg-accent hover:text-foreground [&::-webkit-details-marker]:hidden"
        aria-label="Notificações"
        title="Notificações"
      >
        <Bell className="size-4" />
        {count > 0 && (
          <span className={`absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full px-1 text-[10px] font-semibold text-white ${critical > 0 ? "bg-destructive" : "bg-primary"}`}>
            {count > 99 ? "99+" : count}
          </span>
        )}
      </summary>

      <div className="absolute right-0 top-12 z-50 w-[min(92vw,390px)] overflow-hidden rounded-2xl border border-border/80 bg-popover shadow-2xl">
        <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Precisa de atenção</div>
            <div className="text-[11px] text-muted-foreground">Somente itens registrados no CEO.</div>
          </div>
          {critical > 0 && <span className="rounded-full bg-destructive/10 px-2 py-1 text-[10px] font-medium text-destructive">{critical} urgente{critical === 1 ? "" : "s"}</span>}
        </div>

        <div className="max-h-[min(65vh,520px)] overflow-y-auto p-2">
          {loading && data.items.length === 0 && <div className="p-5 text-center text-sm text-muted-foreground">Atualizando...</div>}
          {!loading && data.items.length === 0 && (
            <div className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
              <CheckCircle2 className="size-5 text-emerald-500" /> Nenhuma pendência imediata.
            </div>
          )}
          {data.items.map((item) => (
            <Link
              key={`${item.type}-${item.id}`}
              to={item.href || "/"}
              onClick={() => { if (detailsRef.current) detailsRef.current.open = false; }}
              className="flex gap-3 rounded-xl p-3 transition hover:bg-accent"
            >
              <div className={`mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg ${item.severity === "critical" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                <AlertCircle className="size-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 text-sm font-medium leading-snug">{item.title}</div>
                <div className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                  {item.client_name && <span className="truncate">{item.client_name}</span>}
                  {item.due_at && <span>{item.due_at.split("-").reverse().join("/")}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {can("can_access_operations") && (
          <div className="border-t border-border/70 p-2">
            <Link to="/operacoes/hoje" onClick={() => { if (detailsRef.current) detailsRef.current.open = false; }} className="block rounded-lg px-3 py-2 text-center text-xs font-medium text-primary hover:bg-primary/5">Ver central de atenção</Link>
          </div>
        )}
      </div>
    </details>
  );
}
